import { deflate, inflate } from "pako";
import { debugError } from "./logs";
import { ApiOptions } from "@/types/strapi";
import { parseUserAgent, UserAgentResult } from "@/lib/userAgent";
import { isPrefetch } from "@/lib/prefetch";

// https://bugs.webkit.org/show_bug.cgi?id=80797
// The JavaScriptCore engine has a hard-coded argument limit of 65536. Most engines
// throw an exception; but there's no normative specification preventing other
// behaviors, such as arbitrarily limiting the number of arguments actually passed to
// the applied function
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/apply#using_apply_and_built-in_functions
const MAX_ARGS_ALLOWED = 65536;

export interface SendBeaconOrFetchOptions {
    skipBeacon?: boolean;
}

const defaultSendBeaconOrFetchOptions: SendBeaconOrFetchOptions = {
    skipBeacon: false,
};

export const sendBeaconOrFetch = async (
    url: string,
    allData: Record<string, unknown>,
    description = "-",
    options: SendBeaconOrFetchOptions = defaultSendBeaconOrFetchOptions
): Promise<void> => {
    if (isPrefetch()) {
        return;
    }

    const opts = { ...defaultSendBeaconOrFetchOptions, ...options };

    const jsonData = JSON.stringify(allData);
    const jsonDataSizeKb = new Blob([jsonData]).size / 1024;

    const startTime = performance.now();

    const parsedUserAgent = parseUserAgent(window.navigator.userAgent || "");

    const reportingPayload: {
        [key: string]:
            | UserAgentResult
            | SendBeaconOrFetchOptions
            | string
            | boolean
            | number
            | null;
    } = {
        message: "sendBeaconOrFetch",
        description,
        userAgent: parsedUserAgent,
        originalSizeInKb: jsonDataSizeKb,
        compressedSizeInKb: null,
        compressionFailed: null,

        uncompressedBeaconSuccess: null,
        compressedBeaconSuccess: null,
        xhrSuccess: null,

        totalExecutionTimeMs: null,
        options: opts,
    };

    if (!opts.skipBeacon) {
        // The beacon is the faster option, but it has a size limit (64 KB)
        // If the data is too large, the beacon will not be queued
        // In that case, we'll attempt to compress the data and send via beacon
        // if that still doesn't work then we will send the data via a XHR POST request
        let beaconQueued = navigator.sendBeacon(url, jsonData);

        // Uncompressed beacon was successful
        if (beaconQueued) {
            reportingPayload.uncompressedBeaconSuccess = true;
            reportingPayload.totalExecutionTimeMs =
                performance.now() - startTime;
            console.info(reportingPayload);
            return;
        }

        // Uncompressed beacon failed
        reportingPayload.uncompressedBeaconSuccess = false;

        // Now compress and try

        // causes a 7ms - 12ms delay (which is NOT noticable)
        // however if the data is STILL too large, it will not be queued
        const compressedJsonData = await compressData(jsonData);

        // could be undefined if the data was even too large to compress
        if (compressedJsonData !== undefined) {
            reportingPayload.compressedSizeInKb =
                new Blob([compressedJsonData]).size / 1024;
            beaconQueued = navigator.sendBeacon(url, compressedJsonData);
            reportingPayload.compressionFailed = false;
        } else {
            reportingPayload.compressionFailed = true;
        }

        // Compressed beacon was successful
        if (beaconQueued) {
            reportingPayload.compressedBeaconSuccess = true;
            reportingPayload.totalExecutionTimeMs =
                performance.now() - startTime;
            console.info(`sendBeaconOrFetch`, reportingPayload);
            return;
        }

        // Compressed beacon failed
        reportingPayload.compressedBeaconSuccess = false;
    }

    let useKeepAlive = true;

    const sizeOfJsonData = new Blob([jsonData]).size / 1024;
    if (sizeOfJsonData > 64) {
        useKeepAlive = false;
        console.warn(
            `sendBeaconOrFetch: Data is too large to send via fetch with keep-alive, disabling keep-alive: ${sizeOfJsonData} KB`
        );
    }

    // causes a 350ms to 600ms delay (which IS noticable)
    // but it's better than not sending the data at all
    await window.fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        keepalive: useKeepAlive,
        body: jsonData,
    });

    reportingPayload.xhrSuccess = true;
    reportingPayload.totalExecutionTimeMs = performance.now() - startTime;

    console.info(`sendBeaconOrFetch`, JSON.stringify(reportingPayload));
};

export const compressData = async (
    jsonData: string
): Promise<string | undefined> => {
    try {
        const jsonDataArray = Array.from(deflate(jsonData));

        // use an `array.join("")` instead of `+=` on a String for performance reasons
        const compressedStringData = [];
        for (let i = 0; i < jsonDataArray.length; i += MAX_ARGS_ALLOWED) {
            compressedStringData.push(
                String.fromCharCode.apply(
                    null,
                    jsonDataArray.slice(
                        i,
                        Math.min(i + MAX_ARGS_ALLOWED, jsonDataArray.length)
                    )
                )
            );
        }

        return compressedStringData.join("");
    } catch (e: unknown) {
        // https://stackoverflow.com/a/52949093/498463
        debugError(
            e,
            "Likely jsonData was too large to convert to string",
            jsonData
        );
        return undefined;
    }
};

export const uncompressData = async (
    possiblyCompressedData: string
): Promise<string | undefined> => {
    let uncompressedData: string | undefined = undefined;
    try {
        const binaryDataLength = possiblyCompressedData.length;
        const buffer = new ArrayBuffer(binaryDataLength);
        const bufferView = new Uint8Array(buffer);
        for (let i = 0; i < binaryDataLength; i++) {
            bufferView[i] = possiblyCompressedData.charCodeAt(i);
        }
        uncompressedData = inflate(bufferView, { to: "string" });
    } catch (e: unknown) {
        debugError(
            e,
            "Data was not a byte array of compressed JSON data",
            possiblyCompressedData,
            Buffer.from(possiblyCompressedData).toString("base64")
        );
    }
    return uncompressedData;
};

export const getAttributionGroup = (): ApiOptions => {
    return {
        populate: {
            AGV: {
                populate: {
                    attribution: {
                        populate: {
                            logo: "*",
                        },
                    },
                    attribution_title: "*",
                    attribution_desc: "*",
                },
            },
        },
    };
};

export const getOfferHeader = (): ApiOptions => {
    return {
        populate: {
            header_image: "*",
            header_image_mobile: "*",
            offer_start_attribution: {
                populate: {
                    attribution_image: "*",
                },
            },
            offer_end_attribution: {
                populate: {
                    attribution_image: "*",
                },
            },
        },
    };
};

export const getAdUnitPopulateQuery = (): ApiOptions => {
    return {
        populate: {
            offerListImageDesktop: "*",
            offerListImageMobile: "*",
            offer: {
                populate: {
                    merchant: {
                        populate: {
                            UsSettings: "*",
                        },
                    },
                    offerImage: "*",
                },
            },
        },
    };
};

/**
 * Parse a string into an array of strings
 * Needed to parse array string from the API response
 * @param arrayString - The string to parse
 * @returns An array of strings
 */
export const parseArrayString = (
    arrayString?: string[] | string | null
): string[] => {
    if (!arrayString) return [];
    if (Array.isArray(arrayString)) return arrayString;
    try {
        return JSON.parse(arrayString);
    } catch (e) {
        return [];
    }
};
